import React, { useState, useEffect, useRef } from 'react';
import cogoToast from 'cogo-toast';
import cn from 'classnames';
import _ from 'lodash';
import { confirmAlert } from 'react-confirm-alert';
import { IoSearchOutline } from 'react-icons/io5';
import { createUrlSiteRule, searchUrlSiteRules, testUrlSiteRule, updateUrlSiteRule } from '../../api/url_site_rules';
import './UrlSiteRules.scss';
import UrlSiteRuleResults from './elements/UrlSiteRuleResults';
import UrlSiteRuleCreate from './elements/UrlSiteRuleCreate';

const URL_SITE_RULE_PAGE_SIZE = 20;

const UrlSiteRules = () => {
  const [domainSearch, setDomainSearch] = useState('');
  const [totalResults, setTotalResults] = useState(0);
  const [curPage, setCurPage] = useState(1);
  const [urlSiteRules, setUrlSiteRules] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSiteRule, setSelectedSiteRule] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isCreateRuleMode, setIsCreateRuleMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const executeSearchSiteRules = async ({ pageNum = 1, isNewSearch = false }) => {
    if (!isNewSearch && urlSiteRules.length >= totalResults) return; // Short-circuit at end of pagination

    const searchParams = {
      searchTerm: domainSearch,
      page: pageNum,
      pageSize: URL_SITE_RULE_PAGE_SIZE
    };

    try {
      const results = await searchUrlSiteRules(searchParams);
      const newSiteRules = results.data || [];
      setUrlSiteRules(isNewSearch ? newSiteRules : [...urlSiteRules, ...newSiteRules]);
      setTotalResults(results.pagination?.totalCount || 0);
    } catch (err) {
      console.error('Error loading URL site rules:', err);
      cogoToast.error(`There was an issue loading URL site rules, please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  const checkForExistingSiteRule = async newRuleDomain => {
    const searchParams = {
      searchTerm: newRuleDomain.trim(),
      exactMatch: true,
      page: 1,
      pageSize: 1
    };

    const results = await searchUrlSiteRules(searchParams);
    return results.data && results.data.length > 0;
  };

  const debouncedSearchSiteRules = async () => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    setIsLoading(true);
    setCurPage(1);
    setSelectedSiteRule(null);
    searchTimeoutRef.current = setTimeout(() => {
      executeSearchSiteRules({ pageNum: 1, isNewSearch: true });
    }, 750);
  };

  const handleSelectNewSiteRule = newSelectedSiteRule => {
    if (hasUnsavedChanges) {
      confirmAlert({
        title: 'Unsaved Changes',
        message: `You have unsaved changes to the site rule for "${selectedSiteRule.domain}". Are you sure you want to exit this site rule without saving?`,
        buttons: [
          { label: 'No', className: 'cancel', onClick: () => {} },
          {
            label: 'Yes',
            onClick: async () => {
              setHasUnsavedChanges(false);
              setSelectedSiteRule(newSelectedSiteRule);
            }
          }
        ]
      });
    } else {
      setSelectedSiteRule(newSelectedSiteRule);
    }
  };

  useEffect(() => {
    debouncedSearchSiteRules();
  }, [domainSearch]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleUrlSiteRuleUpdate = async (UrlSiteRule_id, updatePayload) => {
    try {
      setIsSaving(true);
      const updatedSiteRuleResp = await updateUrlSiteRule(UrlSiteRule_id, updatePayload);
      const updatedSiteRule = updatedSiteRuleResp.data;

      // Update local state
      const updatedSiteRules = urlSiteRules.map(rule => (rule.id === updatedSiteRule.id ? updatedSiteRule : rule));
      setUrlSiteRules(updatedSiteRules);
      selectedSiteRule?.id === updatedSiteRule.id && setSelectedSiteRule(updatedSiteRule);
      cogoToast.success(`Successfully updated site rule!`, { hideAfter: 3 });
    } catch (err) {
      console.error('Error updating URL site rule:', err);
      cogoToast.error(`There was an issue updating the URL site rule, please try again.`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleUrlSiteRuleCreate = async createPayload => {
    try {
      setIsSaving(true);
      const newSiteRuleResp = await createUrlSiteRule(createPayload);
      const newSiteRule = newSiteRuleResp.data;

      // Update local state
      setUrlSiteRules([newSiteRule, ...urlSiteRules]);
      setTotalResults(totalResults + 1);
      setSelectedSiteRule(null);
      setIsCreateRuleMode(false);
      cogoToast.success(`Successfully created site rule!`, { hideAfter: 3 });
    } catch (err) {
      console.error('Error creating URL site rule:', err);
      cogoToast.error(`There was an issue creating the URL site rule, please try again.`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleUrlSiteRuleTest = async testPayload => {
    try {
      const testResp = await testUrlSiteRule(testPayload);

      if (_.isNil(testResp?.data)) {
        cogoToast.error(`There was an issue testing the URL site rule, please check your input and try again.`);
        return null;
      }

      return testResp.data;
    } catch (err) {
      console.error('Error testing URL site rule:', err);
      cogoToast.error(`There was an issue testing the URL site rule, please try again.`);
      return null;
    }
  };

  return (
    <div className='url-site-rules-container'>
      <div className={cn('left-pane', { 'has-selection': selectedSiteRule })}>
        <div className='search'>
          <IoSearchOutline size={20} />
          <input type='text' placeholder='example.com' value={domainSearch} onChange={e => setDomainSearch(e.target.value)} ref={inputRef} />
        </div>
        <div className='results-info'>
          {isLoading ? (
            <span>Loading...</span>
          ) : (
            <div>
              Showing <span>{urlSiteRules.length}</span> of <span>{totalResults}</span> URL site rule{totalResults !== 1 ? 's' : ''}
            </div>
          )}
        </div>
        <UrlSiteRuleResults
          results={urlSiteRules}
          loadNextPage={executeSearchSiteRules}
          curPage={curPage}
          setCurPage={setCurPage}
          selectedSiteRule={selectedSiteRule}
          setSelectedSiteRule={handleSelectNewSiteRule}
        />
      </div>
      <div className={cn('right-pane', { 'has-selection': selectedSiteRule || isCreateRuleMode })}>
        {selectedSiteRule || isCreateRuleMode ? (
          <UrlSiteRuleCreate
            selectedSiteRule={selectedSiteRule}
            handleUrlSiteRuleUpdate={handleUrlSiteRuleUpdate}
            handleUrlSiteRuleCreate={handleUrlSiteRuleCreate}
            setHasUnsavedChanges={setHasUnsavedChanges}
            checkForExistingSiteRule={checkForExistingSiteRule}
            handleUrlSiteRuleTest={handleUrlSiteRuleTest}
            isSaving={isSaving}
          />
        ) : (
          <div className='no-content-container'>
            Select a site rule to edit
            <span>or</span>
            <div className='create-new' onClick={() => setIsCreateRuleMode(true)}>
              Create a new one
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UrlSiteRules;
